import { serverErrorMessage } from '@/services/httpService'
import { CoachClaim, useAnswerClaim, useCoachClaims } from '@/services/coachBenefits'
import moment from 'moment'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { ROUTE_PLAYER_PROFILE } from '@/constants/Routes'

/**
 * Who has claimed a lesson here, and what this coach has to do about it.
 *
 * A queue rather than a log: the rows waiting on an answer come first and are the only ones with
 * buttons on them. A club's equivalent list has no such rows at all — a club credit completes the
 * moment it is claimed — which is why this lives on the coach's side and not in a shared component
 * pretending the two are the same screen.
 *
 * The accept button posts whatever step the server reported as outstanding rather than a hard-coded
 * "accept". That is the whole reason the route is `/steps/{stepKey}`: a claim flow that grows a
 * second step needs no change here.
 */
export const CoachClaimsInbox = ({ kind, entityId }: { kind: string; entityId: number }) => {
  const { t } = useTranslation()
  const { data: claims, isLoading, isError, refetch } = useCoachClaims(kind, entityId)
  const answer = useAnswerClaim(kind, entityId)
  const [error, setError] = useState<string | null>(null)
  // Which claim is mid-decline, so the reason box belongs to one row rather than the whole list.
  const [declining, setDeclining] = useState<number | null>(null)
  const [reason, setReason] = useState('')

  const run = (grantId: number, action: Parameters<typeof answer.mutate>[0]['action'], declineReason?: string) => {
    setError(null)
    answer.mutate(
      { grantId, action, reason: declineReason },
      {
        onSuccess: () => {
          setDeclining(null)
          setReason('')
        },
        // The server's own wording where it has one. "Nothing is waiting on this entity for that
        // claim" is what a coach sees when they answer the same claim in two tabs, and it explains
        // the situation far better than a generic failure would.
        onError: (e) => setError(serverErrorMessage(e, t('coach.answerFailed'))),
      }
    )
  }

  if (isLoading) return <p className="py-8 text-center text-sm text-gray-400">{t('coach.loading')}</p>

  // A failed read is not an empty queue. Telling a coach nobody has claimed with them, because a
  // request timed out, is how a member waits a week for an answer that was never going to come.
  if (isError) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm font-medium text-gray-700">{t('coach.claimsFailedTitle')}</p>
        <p className="mt-1 text-xs text-gray-500">{t('coach.claimsFailedDesc')}</p>
        <button className="mt-3 text-xs text-gray-900 underline" onClick={() => refetch()}>
          {t('coach.tryAgain')}
        </button>
      </div>
    )
  }

  const rows = claims ?? []
  const waiting = rows.filter((claim) => claim.pendingStep !== null)
  const settled = rows.filter((claim) => claim.pendingStep === null)

  if (rows.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm font-medium text-gray-800">{t('coach.noClaimsTitle')}</p>
        <p className="mt-1 text-xs text-gray-500">{t('coach.noClaimsDesc')}</p>
      </div>
    )
  }

  return (
    <div>
      {error && <p className="mb-3 text-xs text-red-600">{error}</p>}

      {waiting.length > 0 && (
        <section>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {t('coach.waitingHeading', { count: waiting.length })}
          </h3>
          <ul className="mt-2 space-y-2">
            {waiting.map((claim) => (
              <li key={claim.grantId} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
                <ClaimSummary claim={claim} />
                {claim.dueAt && (
                  <p className="mt-1 text-xs text-amber-800">
                    {t('coach.answerBy', { date: moment(claim.dueAt).format('D MMM, h:mma') })}
                  </p>
                )}

                {declining === claim.grantId ? (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600" htmlFor={`reason-${claim.grantId}`}>
                      {t('coach.declineReasonLabel')}
                    </label>
                    <input
                      id={`reason-${claim.grantId}`}
                      className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                      placeholder={t('coach.declineReasonPlaceholder')}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        className="rounded border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-white"
                        onClick={() => {
                          setDeclining(null)
                          setReason('')
                        }}
                      >
                        {t('coach.cancel')}
                      </button>
                      <button
                        className="rounded bg-red-600 px-2.5 py-1 text-xs text-white disabled:opacity-40"
                        disabled={answer.isLoading}
                        onClick={() => run(claim.grantId, { decline: true }, reason.trim() || undefined)}
                      >
                        {t('coach.confirmDecline')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      className="rounded-full bg-[#3A79E0] px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2D5FB8] disabled:opacity-40"
                      disabled={answer.isLoading}
                      // The step the SERVER named, not one this screen assumes.
                      onClick={() => run(claim.grantId, { step: claim.pendingStep as string })}
                    >
                      {t('coach.accept')}
                    </button>
                    <button
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-white disabled:opacity-40"
                      disabled={answer.isLoading}
                      onClick={() => setDeclining(claim.grantId)}
                    >
                      {t('coach.decline')}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {settled.length > 0 && (
        <section className={waiting.length > 0 ? 'mt-6' : ''}>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500">{t('coach.acceptedHeading')}</h3>
          <ul className="mt-2 space-y-2">
            {settled.map((claim) => (
              <li key={claim.grantId} className="rounded-lg border border-gray-200 p-3">
                <ClaimSummary claim={claim} />
                {claim.redeemedAt ? (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('coach.usedOn', { date: moment(claim.redeemedAt).format('D MMM YYYY') })}
                  </p>
                ) : (
                  <div className="mt-2">
                    <button
                      className="rounded border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      disabled={answer.isLoading}
                      onClick={() => run(claim.grantId, { redeem: true })}
                    >
                      {t('coach.markUsed')}
                    </button>
                    {/* Marking it used is a note to self, not a gate — DUPR does not police whether
                        the session happened, and saying so stops it reading as a payment step. */}
                    <p className="mt-1 text-xs text-gray-400">{t('coach.markUsedHint')}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

/**
 * The member and what they claimed — enough to recognize someone standing in front of you.
 *
 * The name links to their player profile, mirroring the member's side: a member looking at a
 * pending claim can reach the coach they are waiting on, and a coach deciding whether to accept one
 * should be able to reach the player asking. Both directions of the same pending claim, and it was
 * a dead end in this one.
 */
const ClaimSummary = ({ claim }: { claim: CoachClaim }) => {
  const { t } = useTranslation()
  const worth = claim.worth ?? claim.label
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
      <div className="min-w-0">
        {claim.playerId === undefined ? (
          <p className="truncate text-sm font-medium text-gray-900">{claim.name}</p>
        ) : (
          <Link
            to={`${ROUTE_PLAYER_PROFILE}/${claim.playerId}`}
            className="block truncate text-sm font-medium text-[#2D5FB8] underline-offset-2 hover:underline"
          >
            {claim.name}
          </Link>
        )}
        <p className="text-xs text-gray-500">
          {claim.duprId && <span className="font-mono">{claim.duprId}</span>}
          {claim.duprId && ' · '}
          {t('coach.claimedOn', { date: moment(claim.claimedAt).format('D MMM YYYY') })}
        </p>
      </div>
      {worth && (
        <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">{worth}</span>
      )}
    </div>
  )
}
